import { Layout } from 'antd';
import { Component } from 'react';
import { RedocStandalone } from 'redoc';
import Styled from './App.style';
import Topbar from './components/topbar';
import Changelog from './components/changelog';
import ChangelogGuide from './components/changelog-guide';
import { BrowserRouter } from "react-router-dom";

const { Content } = Layout;

const TAB_CONFIG = {
    intro: {
        path: 'intro',
        aliases: ['introduction'],
        specUrl: '/specs/bukku-intro.yaml',
        sections: {
            introduction: '#section/Introduction',
            'api-access': '#section/API-Access',
            'api-rate-limit': '#section/API-Rate-Limit',
            'usage-advisory': '#section/Usage-Advisory',
            'api-servers': '#section/API-Servers',
            authentication: '#section/Authentication',
            'bearer-token': '#section/Authentication/BearerToken'
        }
    },
    myinvois: {
        path: 'myinvois',
        specUrl: '/specs/bukku-myinvois.yaml',
        sections: {
            availability: '#section/Availability',
            'public-demo-account': '#section/Public-Demo-Account',
            'registering-as-intermediary': '#section/Registering-Bukku-as-an-Intermediary-on-the-MyInvois-Portal',
            'important-notes': '#section/Registering-Bukku-as-an-Intermediary-on-the-MyInvois-Portal/Important-Notes',
            'step-by-step-guide': '#section/Registering-Bukku-as-an-Intermediary-on-the-MyInvois-Portal/Step-by-Step-Guide',
            'preparing-for-myinvois-submission': '#section/Preparing-Company-Account-for-MyInvois-Submission',
            'moving-to-production': '#section/Moving-to-Production'
        }
    },
    sales: {
        path: 'sales',
        specUrl: '/specs/bukku-sales-api.yaml',
        tags: {
            quotation: { tag: 'Quotation', basePath: '/sales/quotes' },
            'sales-order': { tag: 'Sales Order', basePath: '/sales/orders' },
            'delivery-order': { tag: 'Delivery Order', basePath: '/sales/delivery_orders' },
            invoice: { tag: 'Invoice', basePath: '/sales/invoices' },
            'credit-note': { tag: 'Credit Note', basePath: '/sales/credit_notes' },
            payment: { tag: 'Payment', basePath: '/sales/payments' },
            refund: { tag: 'Refund', basePath: '/sales/refunds' }
        }
    },
    purchase: {
        path: 'purchase',
        specUrl: '/specs/bukku-purchase-api.yaml',
        tags: {
            'purchase-order': { tag: 'Purchase Order', basePath: '/purchases/orders' },
            'goods-received-note': { tag: 'Goods Received Note', basePath: '/purchases/goods_received_notes' },
            bill: { tag: 'Bill', basePath: '/purchases/bills' },
            'credit-note': { tag: 'Credit Note', basePath: '/purchases/credit_notes' },
            payment: { tag: 'Payment', basePath: '/purchases/payments', idPath: '/purchases/payments/{id}' },
            refund: { tag: 'Refund', basePath: '/purchases/refunds', idPath: '/purchases/refunds/{id}' }
        }
    },
    bank: {
        path: 'bank',
        specUrl: '/specs/bukku-bank-api.yaml',
        tags: {
            'money-in': { tag: 'Money In', basePath: '/banking/incomes' },
            'money-out': { tag: 'Money Out', basePath: '/banking/expenses' },
            transfer: { tag: 'Transfers', basePath: '/banking/transfers' }
        }
    },
    contact: {
        path: 'contact',
        specUrl: '/specs/bukku-contacts-api.yaml',
        tags: {
            contact: { tag: 'Contacts', basePath: '/contacts', singular: 'contact', archive: true },
            group: { tag: 'Groups', basePath: '/contacts/groups', singular: 'contact group' }
        }
    },
    product: {
        path: 'product',
        specUrl: '/specs/bukku-product-api.yaml',
        tags: {
            product: { tag: 'Product', basePath: '/products', archive: true },
            'product-bundle': { tag: 'Bundles', basePath: '/products/bundles', singular: 'product bundle', archive: true },
            group: { tag: 'Groups', basePath: '/products/groups', singular: 'product group' }
        }
    },
    stock: {
        path: 'stock',
        specUrl: '/specs/bukku-stock-api.yaml',
        tags: {
            'stock-adjustment': { tag: 'Stock Adjustment', basePath: '/stock/adjustments', singular: 'stock adjustment' },
            'stock-transfer': { tag: 'Stock Transfer', basePath: '/stock/transfers', singular: 'stock transfer' }
        }
    },
    accounting: {
        path: 'accounting',
        specUrl: '/specs/bukku-accounting-api.yaml',
        tags: {
            'journal-entry': { tag: 'Journal Entries', basePath: '/journal_entries', singular: 'journal entry' },
            account: { tag: 'Account', basePath: '/accounts', archive: true }
        }
    },
    lists: {
        path: 'lists',
        specUrl: '/specs/bukku-lists-v2-api.yaml',
        tags: {
            lists: {
                tag: 'Lists',
                operations: {
                    read: { path: '/v2/lists', method: 'post' }
                }
            }
        },
        defaultHash: '#tag/Lists/paths/~1v2~1lists/post'
    },
    files: {
        path: 'files',
        specUrl: '/specs/bukku-files-api.yaml',
        tags: {
            file: {
                tag: 'Files',
                basePath: '/files',
                operations: {
                    upload: { path: '/files', method: 'post' }
                }
            }
        }
    },
    'control-panel': {
        path: 'control-panel',
        specUrl: '/specs/bukku-control-panel-api.yaml',
        tags: {
            location: { tag: 'Locations', basePath: '/locations', idPath: '/location/{id}', archive: true },
            tag: { tag: 'Tags', basePath: '/tags' },
            'tag-group': { tag: 'Tag Groups', basePath: '/tags/groups', singular: 'tag group' }
        }
    },
    changelog: {
        path: 'changelog'
        // Custom-rendered by <Changelog />; intentionally no specUrl/sections/tags.
    },
    partner: {
        path: 'partner',
        specUrl: '/specs/bukku-partner-api.yaml',
        tags: {
            plans: {
                tag: 'Plans',
                operations: {
                    information: { path: '/plans', method: 'get' }
                }
            },
            client: {
                tag: 'Client',
                operations: {
                    creation: { path: '/partners/current/clients', method: 'post' },
                    'subscription-cancellation': { path: '/partners/current/clients/{companyId}/cancel', method: 'post' }
                }
            }
        }
    }
};

const PATH_TO_TAB_KEY = Object.entries(TAB_CONFIG).reduce((tabs, [key, config]) => {
    tabs[config.path] = key;

    if (config.aliases) {
        config.aliases.forEach((alias) => {
            tabs[alias] = key;
        });
    }

    return tabs;
}, {});

const LEGACY_HASH_TAB_MAP = {
    '#tag/Quotation': 'sales'
};

const METHOD_ACTIONS = {
    post: 'create',
    put: 'update',
    delete: 'delete'
};

const normalizePathname = (pathname) => pathname.replace(/^\/+|\/+$/g, '');

const encodeRedocPath = (path) => path.replace(/\//g, '~1');

const encodeRedocTag = (tag) => tag.replace(/\s+/g, '-');

const operationHash = (tag, path, method) => (
    `#tag/${encodeRedocTag(tag)}/paths/${encodeRedocPath(path)}/${method}`
);

const getIdPath = (resource) => (
    resource.idPath || `${resource.basePath}/{transactionId}`
);

const getOperationFromAction = (resource, action) => {
    if (resource.operations && resource.operations[action]) {
        return resource.operations[action];
    }

    if (action === 'read-all') {
        if (!resource.basePath) {
            return null;
        }

        return { path: resource.basePath, method: 'get' };
    }

    if (action === 'read') {
        if (!resource.basePath) {
            return null;
        }

        return { path: getIdPath(resource), method: 'get' };
    }

    if (action === 'archive-unarchive' && resource.archive) {
        if (!resource.basePath) {
            return null;
        }

        return { path: getIdPath(resource), method: 'patch' };
    }

    if (action === 'update-status') {
        if (!resource.basePath) {
            return null;
        }

        return { path: getIdPath(resource), method: 'patch' };
    }

    const method = Object.keys(METHOD_ACTIONS).find((key) => METHOD_ACTIONS[key] === action);

    if (!method) {
        return null;
    }

    if (!resource.basePath) {
        return null;
    }

    return {
        path: method === 'post' ? resource.basePath : getIdPath(resource),
        method
    };
};

const getTabPath = (tabKey) => {
    if (tabKey === 'intro') {
        return 'introduction';
    }

    return TAB_CONFIG[tabKey].path;
};

const getCustomPath = (tabKey, resourceSlug, actionSlug) => {
    const tabPath = getTabPath(tabKey);

    if (!resourceSlug) {
        return `/${tabPath}`;
    }

    if (!actionSlug) {
        return `/${tabPath}/${resourceSlug}`;
    }

    return `/${tabPath}/${resourceSlug}/${actionSlug}`;
};

const addHashRoute = (routes, redocHash, customPath) => {
    routes[redocHash] = customPath;
};

const buildHashRouteMaps = () => {
    const routeMaps = {};

    Object.entries(TAB_CONFIG).forEach(([tabKey, tabConfig]) => {
        const routes = {};

        if (tabConfig.sections) {
            Object.entries(tabConfig.sections).forEach(([sectionSlug, hash]) => {
                addHashRoute(routes, hash, sectionSlug === getTabPath(tabKey)
                    ? `/${getTabPath(tabKey)}`
                    : getCustomPath(tabKey, sectionSlug));
            });
        }

        if (tabConfig.tags) {
            Object.entries(tabConfig.tags).forEach(([resourceSlug, resource]) => {
                const sectionPath = tabKey === 'lists' ? '/lists' : getCustomPath(tabKey, resourceSlug);
                addHashRoute(routes, `#tag/${encodeRedocTag(resource.tag)}`, sectionPath);

                if (resource.operations) {
                    Object.entries(resource.operations).forEach(([actionSlug, operation]) => {
                        const path = tabKey === 'lists' && actionSlug === 'read'
                            ? '/lists'
                            : getCustomPath(tabKey, resourceSlug, actionSlug);

                        addHashRoute(routes, operationHash(resource.tag, operation.path, operation.method), path);
                    });
                }

                ['read-all', 'read', 'create', 'update', 'update-status', 'archive-unarchive', 'delete'].forEach((actionSlug) => {
                    const operation = getOperationFromAction(resource, actionSlug);

                    if (!operation) {
                        return;
                    }

                    const hash = operationHash(resource.tag, operation.path, operation.method);

                    if (!routes[hash]) {
                        addHashRoute(routes, hash, getCustomPath(tabKey, resourceSlug, actionSlug));
                    }
                });
            });
        }

        routeMaps[tabKey] = routes;
    });

    return routeMaps;
};

const REDOC_HASH_TO_CUSTOM_PATH_BY_TAB = buildHashRouteMaps();

// True only when served over loopback — i.e. a browser on the same machine running
// `yarn start` (localhost / 127.x / ::1). Deliberately excludes private LAN IPs so the
// /playground demo cannot appear on the hosted server even if it sits on a LAN address.
const isLocalHost = () => {
    const host = window.location.hostname;

    return host === 'localhost' || host === '[::1]' || /^127\./.test(host);
};

const resolveCustomRoute = () => {
    const segments = normalizePathname(window.location.pathname).split('/').filter(Boolean);
    const hash = window.location.hash;

    if (!segments.length) {
        const legacyTabKey = LEGACY_HASH_TAB_MAP[hash];

        return {
            activeTabKey: legacyTabKey || 'intro',
            targetHash: hash || null,
            customPath: legacyTabKey ? getCustomPath(legacyTabKey) : '/'
        };
    }

    const [tabSlug, resourceSlug, actionSlug] = segments;

    // Hidden, tab-less route: a standalone authoring guide demonstrating the changelog
    // component's capabilities. Reachable only by URL, and only on a local machine —
    // on the hosted server the segment is ignored and falls through to the intro.
    if (tabSlug === 'changelog-guide' && isLocalHost()) {
        return { activeTabKey: 'changelog-guide', targetHash: null, customPath: '/changelog-guide' };
    }

    const activeTabKey = PATH_TO_TAB_KEY[tabSlug] || 'intro';
    const tabConfig = TAB_CONFIG[activeTabKey];

    if (activeTabKey === 'lists' && resourceSlug === 'read') {
        return {
            activeTabKey,
            targetHash: tabConfig.defaultHash,
            customPath: '/lists/read'
        };
    }

    if (!resourceSlug) {
        return {
            activeTabKey,
            targetHash: activeTabKey === 'lists' ? tabConfig.defaultHash : hash || null,
            customPath: activeTabKey === 'intro' ? '/introduction' : `/${tabConfig.path}`
        };
    }

    if (tabConfig.sections && tabConfig.sections[resourceSlug]) {
        return {
            activeTabKey,
            targetHash: tabConfig.sections[resourceSlug],
            customPath: getCustomPath(activeTabKey, resourceSlug)
        };
    }

    const resource = tabConfig.tags && tabConfig.tags[resourceSlug];

    if (!resource) {
        return {
            activeTabKey,
            targetHash: hash || null,
            customPath: `/${segments.join('/')}`
        };
    }

    if (!actionSlug) {
        return {
            activeTabKey,
            targetHash: `#tag/${encodeRedocTag(resource.tag)}`,
            customPath: getCustomPath(activeTabKey, resourceSlug)
        };
    }

    const operation = getOperationFromAction(resource, actionSlug);

    return {
        activeTabKey,
        targetHash: operation ? operationHash(resource.tag, operation.path, operation.method) : `#tag/${encodeRedocTag(resource.tag)}`,
        customPath: getCustomPath(activeTabKey, resourceSlug, actionSlug)
    };
};

class App extends Component {
    constructor(props) {
        super(props);

        const route = resolveCustomRoute();

        this.state = {
            activeTabKey: route.activeTabKey,
            targetHash: route.targetHash,
            customPath: route.customPath
        };
    }

    redocOptions = {
        hideDownloadButton: true,
        // disableSearch: true,
        hideSchemaTitles: true,
        jsonSampleExpandLevel: 'all',
        pathInMiddlePanel: true
    };

    componentDidMount() {
        window.addEventListener('popstate', this.syncRouteFromUrl);
        window.addEventListener('hashchange', this.syncRouteFromUrl);
        document.addEventListener('click', this.handleRedocLinkClick);
        this.rewriteRedocLinks();
        this.scrollToTargetHash();
    }

    componentDidUpdate(prevProps, prevState) {
        if (
            prevState.activeTabKey !== this.state.activeTabKey ||
            prevState.targetHash !== this.state.targetHash
        ) {
            this.rewriteRedocLinks();
            this.scrollToTargetHash();
        }
    }

    componentWillUnmount() {
        window.removeEventListener('popstate', this.syncRouteFromUrl);
        window.removeEventListener('hashchange', this.syncRouteFromUrl);
        document.removeEventListener('click', this.handleRedocLinkClick);
    }

    syncRouteFromUrl = () => {
        const route = resolveCustomRoute();

        this.setState({
            activeTabKey: route.activeTabKey,
            targetHash: route.targetHash,
            customPath: route.customPath
        });
    };

    handleRedocLinkClick = (event) => {
        if (this.isActivatingRedocHash) {
            return;
        }

        const anchor = event.target.closest && event.target.closest('a[data-redoc-hash]');

        if (!anchor) {
            return;
        }

        const targetHash = anchor.getAttribute('data-redoc-hash');
        const customPath = anchor.getAttribute('href');

        if (!targetHash || !customPath || !customPath.startsWith('/')) {
            return;
        }

        event.preventDefault();

        window.history.pushState(null, '', customPath);

        const route = resolveCustomRoute();

        this.setState({
            activeTabKey: route.activeTabKey,
            targetHash: targetHash,
            customPath
        });
    };

    rewriteRedocLinks = () => {
        window.clearTimeout(this.rewriteLinksTimeout);

        this.rewriteLinksTimeout = window.setTimeout(() => {
            const hashRouteMap = REDOC_HASH_TO_CUSTOM_PATH_BY_TAB[this.state.activeTabKey] || {};

            document.querySelectorAll('a[href], a[data-redoc-hash]').forEach((anchor) => {
                const redocHash = anchor.getAttribute('data-redoc-hash') || anchor.getAttribute('href');
                const customPath = hashRouteMap[redocHash];

                if (!customPath) {
                    return;
                }

                anchor.setAttribute('data-redoc-hash', redocHash);
                anchor.setAttribute('href', customPath);
            });
        }, 300);
    };

    activateRedocHash = (targetHash) => {
        const anchor = Array.from(document.querySelectorAll('a[href], a[data-redoc-hash]'))
            .find((element) => (
                element.getAttribute('data-redoc-hash') === targetHash ||
                element.getAttribute('href') === targetHash
            ));

        if (!anchor) {
            return false;
        }

        const hashRouteMap = REDOC_HASH_TO_CUSTOM_PATH_BY_TAB[this.state.activeTabKey] || {};
        const cleanPath = this.state.customPath || hashRouteMap[targetHash] || window.location.pathname;
        const currentHref = anchor.getAttribute('href');

        this.isActivatingRedocHash = true;
        anchor.setAttribute('href', targetHash);
        anchor.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
        anchor.setAttribute('href', currentHref);
        this.isActivatingRedocHash = false;

        window.setTimeout(() => {
            window.history.replaceState(null, '', cleanPath);
            this.rewriteRedocLinks();
        }, 0);

        return true;
    };

    scrollToTargetHash = () => {
        const { targetHash } = this.state;

        if (!targetHash) {
            return;
        }

        window.clearTimeout(this.scrollTimeout);

        this.scrollTimeout = window.setTimeout(() => {
            this.rewriteRedocLinks();

            if (this.activateRedocHash(targetHash)) {
                return;
            }

            const link = Array.from(document.querySelectorAll('a[href], a[data-redoc-hash]'))
                .find((element) => (
                    element.getAttribute('data-redoc-hash') === targetHash ||
                    element.getAttribute('href') === targetHash
                ));
            const target = link && link.closest('h1, h2, h3, h4, h5, h6, section, div');

            if (target) {
                target.scrollIntoView();
            }
        }, 700);
    };

    setActiveTabKey = (key) => {
        const tabConfig = TAB_CONFIG[key] || TAB_CONFIG.intro;
        const nextPath = key === 'intro' ? '/' : `/${tabConfig.path}`;

        window.history.pushState(null, '', nextPath);

        this.setState({
            activeTabKey: key,
            targetHash: key === 'lists' ? tabConfig.defaultHash : null,
            customPath: nextPath
        });
    };

    getSpecUrlByKey = () => {
        const {
            activeTabKey,
        } = this.state;

        return (TAB_CONFIG[activeTabKey] || TAB_CONFIG.intro).specUrl;
    };

    render() {
        const {
            activeTabKey,
        } = this.state;

        return (
            <BrowserRouter>
                <Styled>
                    <Layout>
                        <Topbar activeTabKey={activeTabKey} setActiveTabKey={this.setActiveTabKey} />
                        <Content>
                            {activeTabKey === 'changelog-guide'
                                ? <ChangelogGuide />
                                : activeTabKey === 'changelog'
                                ? <Changelog />
                                : <RedocStandalone
                                    key={activeTabKey}
                                    options={this.redocOptions}
                                    specUrl={this.getSpecUrlByKey()}
                                />}
                        </Content>
                    </Layout>
                </Styled>
            </BrowserRouter>
        );
    }
}
export default App;
